import { useCallback, useMemo, useState } from 'react';
import {
  identityLevelsChannel,
  type LevelsChannel,
  type LevelsChannelConfig,
  type LevelsValue,
} from '../../lib/adjustments/levels';
import { RangeRow } from './RangeRow';
import type { AdjustmentPanelProps } from './registry';

const CHANNELS: { value: LevelsChannel; label: string; color: string }[] = [
  { value: 'rgb', label: 'RGB', color: '#888' },
  { value: 'r', label: 'R', color: '#ef4444' },
  { value: 'g', label: 'G', color: '#22c55e' },
  { value: 'b', label: 'B', color: '#3b82f6' },
];

const HISTOGRAM_HEIGHT = 96;
const HISTOGRAM_WIDTH = 256;

export function LevelsPanel({ value, onChange, histogram }: AdjustmentPanelProps<LevelsValue>) {
  const [activeChannel, setActiveChannel] = useState<LevelsChannel>('rgb');

  const channel = value[activeChannel];

  const updateChannel = useCallback(
    (patch: Partial<LevelsChannelConfig>) => {
      onChange({
        ...value,
        [activeChannel]: { ...value[activeChannel], ...patch },
      });
    },
    [activeChannel, onChange, value],
  );

  const resetActiveChannel = useCallback(() => {
    onChange({ ...value, [activeChannel]: identityLevelsChannel() });
  }, [activeChannel, onChange, value]);

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        {CHANNELS.map((ch) => (
          <button
            key={ch.value}
            type="button"
            className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
              activeChannel === ch.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-accent'
            }`}
            onClick={() => setActiveChannel(ch.value)}
          >
            {ch.label}
          </button>
        ))}
      </div>

      <Histogram histogram={activeChannel === 'rgb' ? histogram : undefined} config={channel} />

      <fieldset className="space-y-2">
        <legend className="text-[11px] font-medium uppercase text-muted-foreground tracking-wide mb-1">
          Input
        </legend>
        <RangeRow
          label="Black"
          min={0}
          max={Math.max(0, channel.inputWhite - 1)}
          step={1}
          value={channel.inputBlack}
          onChange={(v) => updateChannel({ inputBlack: v })}
        />
        <RangeRow
          label="Gamma"
          min={0.1}
          max={9.99}
          step={0.01}
          value={channel.gamma}
          onChange={(v) => updateChannel({ gamma: v })}
          decimals={2}
        />
        <RangeRow
          label="White"
          min={Math.min(255, channel.inputBlack + 1)}
          max={255}
          step={1}
          value={channel.inputWhite}
          onChange={(v) => updateChannel({ inputWhite: v })}
        />
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-[11px] font-medium uppercase text-muted-foreground tracking-wide mb-1">
          Output
        </legend>
        <RangeRow
          label="Black"
          min={0}
          max={Math.max(0, channel.outputWhite - 1)}
          step={1}
          value={channel.outputBlack}
          onChange={(v) => updateChannel({ outputBlack: v })}
        />
        <RangeRow
          label="White"
          min={Math.min(255, channel.outputBlack + 1)}
          max={255}
          step={1}
          value={channel.outputWhite}
          onChange={(v) => updateChannel({ outputWhite: v })}
        />
      </fieldset>

      <button
        type="button"
        className="text-[11px] text-muted-foreground hover:text-foreground"
        onClick={resetActiveChannel}
      >
        Reset {CHANNELS.find((c) => c.value === activeChannel)?.label} channel
      </button>
    </div>
  );
}

/** Histogram backdrop with three under-histogram markers showing
 *  the current input black / gamma / white positions. The markers
 *  aren't draggable in v1 — the sliders below are the input. The
 *  visualisation lets the user line up the input range against the
 *  data without doing the math in their head. */
function Histogram({ histogram, config }: { histogram?: number[]; config: LevelsChannelConfig }) {
  const histPath = useMemo(() => {
    if (!histogram || histogram.length !== 256) return '';
    const max = Math.log(1 + Math.max(...histogram));
    if (max === 0) return '';
    let d = `M 0 ${HISTOGRAM_HEIGHT}`;
    for (let x = 0; x < 256; x++) {
      const h = (Math.log(1 + histogram[x]) / max) * HISTOGRAM_HEIGHT;
      d += ` L ${x} ${HISTOGRAM_HEIGHT - h}`;
    }
    d += ` L 255 ${HISTOGRAM_HEIGHT} Z`;
    return d;
  }, [histogram]);

  // Gamma marker x position: where input value goes in vs where it
  // would land at gamma=1. We render it at the value that maps to
  // 0.5 in the normalised t — i.e., at inputBlack + 0.5^gamma * span.
  // Lets the user see the midtone "anchor" shift as they drag gamma.
  const span = Math.max(1, config.inputWhite - config.inputBlack);
  const gammaMidNormalised = Math.pow(0.5, config.gamma);
  const gammaMarker = config.inputBlack + gammaMidNormalised * span;

  return (
    <div className="space-y-1 select-none">
      <div className="bg-muted rounded-md p-2">
        <svg
          viewBox={`0 0 ${HISTOGRAM_WIDTH} ${HISTOGRAM_HEIGHT}`}
          className="w-full h-auto"
          style={{ aspectRatio: `${HISTOGRAM_WIDTH} / ${HISTOGRAM_HEIGHT}` }}
        >
          {histPath && <path d={histPath} fill="rgba(150, 150, 150, 0.45)" />}
          <g stroke="rgba(128, 128, 128, 0.3)" strokeWidth="1">
            {[64, 128, 192].map((g) => (
              <line key={g} x1={g} y1="0" x2={g} y2={HISTOGRAM_HEIGHT} />
            ))}
          </g>
          {/* Input range overlay — region outside [inputBlack, inputWhite] is dimmed. */}
          {config.inputBlack > 0 && (
            <rect
              x={0}
              y={0}
              width={config.inputBlack}
              height={HISTOGRAM_HEIGHT}
              fill="rgba(0,0,0,0.35)"
            />
          )}
          {config.inputWhite < 255 && (
            <rect
              x={config.inputWhite}
              y={0}
              width={255 - config.inputWhite}
              height={HISTOGRAM_HEIGHT}
              fill="rgba(255,255,255,0.18)"
            />
          )}
        </svg>
      </div>
      {/* Marker triangles below the histogram. Inline width with the
          SVG above by using the same 256-unit grid via percentage. */}
      <div className="relative h-3 px-2">
        <Marker positionPct={(config.inputBlack / 255) * 100} fill="#000" stroke="#fff" />
        <Marker positionPct={(gammaMarker / 255) * 100} fill="#888" stroke="#fff" />
        <Marker positionPct={(config.inputWhite / 255) * 100} fill="#fff" stroke="#000" />
      </div>
    </div>
  );
}

function Marker({
  positionPct,
  fill,
  stroke,
}: {
  positionPct: number;
  fill: string;
  stroke: string;
}) {
  // 8px-wide triangle, centred on positionPct. `pointer-events:none`
  // so the markers don't intercept clicks meant for the slider rows.
  return (
    <div
      aria-hidden
      className="absolute top-0 pointer-events-none"
      style={{
        left: `calc(${positionPct}% - 4px)`,
        width: 8,
        height: 8,
      }}
    >
      <svg viewBox="0 0 8 8" width="8" height="8">
        <polygon points="4,0 8,8 0,8" fill={fill} stroke={stroke} strokeWidth="0.75" />
      </svg>
    </div>
  );
}
